import { Form, FormikProps } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { VALUABLE_ABOUT, VALUABLE_PRICE } from "../valuablesConstants";
import {
  ProductIntroFormValues,
  ValuablesPriceRequestInfo,
  ValuablesText,
} from "../valuablesInterface";

import { ErrorBoundary } from "@sentry/react";
import { StyledAlert } from "App/global-style";
import { MarketingConsent } from "Components/Consent/MarketingConsent";
import { ProductIntroTile } from "Components/Features/CheckPrice/Tiles/ProductIntroTile";
import { PartnerProductIntroForms } from "Components/Features/CheckPrice/Tiles/ProductIntroTile/PartnerProductIntroForms/PartnerProductIntroForms";
import { SystemErrorAlert } from "Components/Form/Alert/SystemErrorAlert";
import { ErrorsSummedUp } from "Components/Form/ErrorsSummedUp";
import { NavigationButtons } from "Components/NavigationButtons";
import { ScrollToFirstError } from "Components/ScrollToFirstError";
import { useTexts } from "Components/TextContext";
import { ProductBackendName } from "Features/CheckPrice/Products/productTypes";
import { useFormState } from "Store/priceCalculation/priceCalculationHooks";
import { updatePriceCalculationFormState } from "Store/priceCalculation/priceCalculationSlice";
import { ReduxState } from "Store/rootReducer";
import { TextLanguage } from "Store/texts/interfaces";
import { mapValidationErrors } from "Utils/formUtils";
import { isDefined } from "Utils/functions";
import React from "react";
import { extractLabels } from "../valuablesUtils/extractLabels";
import { mapFormToPriceRequest } from "../valuablesUtils/mapFormToPriceRequest";
import { ValuablesProductIntroFormElements } from "./FormElements";
import { validationSchema } from "./valuablesProductIntroValidation";

interface Props {
  sendPriceRequest: (payload: ValuablesPriceRequestInfo) => void;
  texts: ValuablesText;
  lang: TextLanguage;
}

export const ValuablesProductIntro = (props: Props): React.ReactElement => {
  const { sendPriceRequest, texts, lang } = props;
  const { getText } = useTexts();
  const initialFormValues = useFormState(VALUABLE_ABOUT.id, initialValues);
  const priceFormValues = useFormState(VALUABLE_PRICE.id);
  const dispatch = useDispatch();

  const submitHandler = (formValues: any): void => {
    dispatch(
      updatePriceCalculationFormState({
        stepId: stepId,
        formState: formValues,
      }),
    );
    sendPriceRequest(mapFormToPriceRequest(formValues));
  };

  const { isLoading, error } = useSelector(
    (state: ReduxState) => state.priceCalculation.priceCalculationResult,
  );

  return (
    <ErrorBoundary
      beforeCapture={(scope) =>
        scope.setTag(
          "location",
          `CheckPrice - ProductIntro - ${ProductBackendName.VALUABLES}`,
        )
      }
    >
      <ProductIntroTile
        initialValues={{
          ...initialFormValues,
          coverage_amount:
            "coverage_amount" in priceFormValues
              ? priceFormValues.coverage_amount
              : initialFormValues.coverage_amount,
        }}
        productBackendName={ProductBackendName.VALUABLES}
        validationSchema={validationSchema(getText)}
        onSubmit={submitHandler}
      >
        {(formProps: FormikProps<ProductIntroFormValues>) => (
          <Form>
            <ValuablesProductIntroFormElements formProps={formProps} />

            <StyledAlert
              title={getText("valuablesform.alerttitle.original_receipt")}
              description={getText(
                "valuablesform.alertdescription.original_receipt",
              )}
              transparent
            />

            <PartnerProductIntroForms isWithSurface />

            <ErrorsSummedUp
              errors={mapValidationErrors(
                formProps.errors,
                extractLabels(texts, formProps.values.product),
                lang,
              )}
              isVisible
            />

            {isDefined(error) && (
              <SystemErrorAlert alertId="priceCalculation" />
            )}

            <ScrollToFirstError />
            <MarketingConsent />
            <NavigationButtons isLoading={isLoading} hidePreviousButton />
          </Form>
        )}
      </ProductIntroTile>
    </ErrorBoundary>
  );
};

const stepId = VALUABLE_ABOUT.id;
const initialValues = {
  product: undefined,
  year: undefined,
  description: undefined,
  coverage_amount: undefined,
  injuries: undefined,
  injuryYear: undefined,
};
